import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ImageListItem, Job, PropertyPhoto } from '../../model/tops.models';
import { timestampLabel } from '../../mixins/date-utils';
import { ApiService } from '../../service/api.service';
import { CameraChannel, CameraChannelService } from '../../service/camera-channel.service';
import { IndexedDbStorageService } from '../../service/indexed-db-storage.service';
import { ToastService } from '../../service/toast.service';
import { concatMap, forkJoin, from, map, of, switchMap, toArray } from 'rxjs';
@Component({
  selector: 'app-camera-popout',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './camera-popout.component.html',
  styleUrl: './camera-popout.component.css',
})
export class CameraPopoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly cameraChannels = inject(CameraChannelService);
  private readonly storage = inject(IndexedDbStorageService);

  @ViewChild('video') video?: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvas?: ElementRef<HTMLCanvasElement>;

  @Input() jobIdInput: number | string | null = null;
  @Input() modalMode = false;
  @Input() initialImageName: string | null = null;
  @Input() allowBlankImageName = false;
  @Output() closed = new EventEmitter<void>();
  @Output() photoSaved = new EventEmitter<string | null | undefined>();

  jobId = '';
  job: Job | null = null;
  buildings: any[] = [];
  selectedBuildingId: string = '';
  imageList: ImageListItem[] = [];
  photos: PropertyPhoto[] = [];
  displayOrderOptions = Array.from({ length: 101 }, (_, index) => index.toString().padStart(2, '0'));
  devices: MediaDeviceInfo[] = [];
  selectedDeviceId = '';
  deletingPhotoId: number | null = null;
  captureButtonPosition: 'left' | 'right' = 'right';
  formAlive = true;
  capturing = false;
  cameraStarting = false;
  cameraError = '';
  stream: MediaStream | null = null;
  channel: CameraChannel | null = null;
  watchdogId: number | null = null;
  pendingPhotoData = '';
  pendingPhotoName = '';
  pendingPhotoImageType = '';
  pendingPhotoDescription = '';
  pendingPhotoDisplayOrder = '';
  savingPendingPhoto = false;
  editingPhoto: PropertyPhoto | null = null;
  editingPhotoName = '';
  editingPhotoImageType = '';
  editingPhotoDescription = '';
  editingPhotoDisplayOrder = '';
  savingPhotoEdit = false;

  ngOnInit(): void {
    void this.loadHandedness();
    this.jobId = String(this.jobIdInput || this.route.snapshot.paramMap.get('jobId') || '');
    this.channel = this.cameraChannels.create(this.jobId);
    this.channel.subscribe((message) => {
      if (message.type === 'form-closing') this.closeWindow();
      if (message.type === 'photo-saved') this.loadPhotos();
    });
    if (!this.modalMode) {
      this.watchdogId = window.setInterval(() => {
        if (!window.opener || window.opener.closed) this.closeWindow();
      }, 1000);
    }
    this.load();
  }

  ngAfterViewInit(): void {
    this.loadDevices();
  }

  ngOnDestroy(): void {
    this.stopStream();
    if (this.watchdogId) window.clearInterval(this.watchdogId);
    this.channel?.close();
  }

  load(): void {
    forkJoin({ job: this.api.getJob(this.jobId), info: this.api.getPropertyInfo(this.jobId) }).subscribe({
      next: ({ job, info }) => { this.job = job; if (!this.modalMode) document.title = job.address ? `Camera - ${job.address}` : 'Camera'; 
      this.extractBuildings(info); },
      error: () => {},
    });
    this.loadPhotos();
    this.loadImageList();
  }

  loadImageList(): void {
    this.api.getImageList().subscribe({ next: (imageList) => this.imageList = imageList, error: () => {} });
  }

  loadPhotos(): void {
    this.api.getPendingUploadPhotos(this.jobId).subscribe({ next: (photos) => this.photos = [...photos].reverse(), error: () => {} });
  }

  async loadDevices(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError = 'Camera access is not available in this browser. Open the app from http://localhost:4200 or use a browser with camera support.';
      this.toast.error('Camera unavailable', this.cameraError);
      return;
    }

    try {
      await this.refreshDevices();
      await this.startStream();
    } catch {
      this.toast.error('Camera unavailable', 'Unable to enumerate camera devices.');
    }
  }

  async startStream(): Promise<void> {
    this.stopStream();
    this.cameraStarting = true;
    this.cameraError = '';

    const constraints = this.cameraConstraints();
    let lastError: unknown = null;

    for (const video of constraints) {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
        if (this.video?.nativeElement) {
          this.video.nativeElement.srcObject = this.stream;
          await this.video.nativeElement.play().catch(() => undefined);
        }
        this.selectedDeviceId = this.currentVideoDeviceId() || this.selectedDeviceId;
        await this.refreshDevices();
        this.cameraStarting = false;
        return;
      } catch (error) {
        lastError = error;
        if (!this.shouldTryNextConstraint(error)) break;
      }
    }

    this.cameraStarting = false;
    this.cameraError = this.cameraErrorMessage(lastError);
    this.toast.error('Camera unavailable', this.cameraError);
  }

  stopStream(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    if (this.video?.nativeElement) this.video.nativeElement.srcObject = null;
  }

  setHandedness(position: 'left' | 'right'): void {
    this.captureButtonPosition = position;
    this.storage.setItem('tops-camera-handedness', position);
  }

  private async loadHandedness(): Promise<void> {
    await this.storage.ready();
    const position = this.storage.getItem('tops-camera-handedness');
    if (position === 'left' || position === 'right') this.captureButtonPosition = position;
  }

  extractBuildings(propertyInfo: any): void {
  if (!propertyInfo) {
    this.buildings = [];
    return;
  }

  const buildingList: any[] = [];

 
  let parsedBuildings: any[] = [];  
      try {
        // Since "buildings" is a JSON string, we parse it here
        if (typeof propertyInfo.buildings === 'string') {
          parsedBuildings = JSON.parse(propertyInfo.buildings);
        } else {
          parsedBuildings = propertyInfo.buildings;
        }
      } catch {
        parsedBuildings = [];
      }

      // Ensure we have an array to iterate over
      if (Array.isArray(parsedBuildings)) {
        parsedBuildings.forEach((building) => {
          // Normalizing properties to match what your HTML template expects
          if(building.isDelete === false) {
            buildingList.push({
              MultiDwellingImprovementID: building.id,
              dwellingName: building.name
            });
          }
        });
      }
    
 

  this.buildings = buildingList;

  // Set default selection to the first building's ID if available
  // if (this.buildings.length > 0) {
  //   this.selectedBuildingId = this.buildings[0].MultiDwellingImprovementID || '';
  // }
 }
 
 
  onBuildingChange(): void {
  }


  async capture(): Promise<void> {
    const video = this.video?.nativeElement;
    const canvas = this.canvas?.nativeElement;
    if (!video || !canvas || this.capturing || this.pendingPhotoData || !this.formAlive || !this.stream) return;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    this.capturing = true;
    try {
      this.pendingPhotoData = canvas.toDataURL('image/png');
      this.pendingPhotoName = this.initialImageName ?? timestampLabel('Photo');
      this.pendingPhotoImageType = '';
      this.pendingPhotoDescription = '';
      this.pendingPhotoDisplayOrder = '';
      this.capturing = false;
    } catch (error) {
      this.capturing = false;
      this.toast.error('Error', error instanceof Error ? error.message : 'Failed to process photo.');
    }
  }

  savePendingPhoto(): void {
    if (!this.pendingPhotoData || this.savingPendingPhoto) return;
    const name = this.allowBlankImageName ? (this.pendingPhotoName.trim() || null) : (this.pendingPhotoName.trim() || timestampLabel('Photo'));
    const imageType = this.pendingPhotoImageType.trim() || null;
    const displayOrder = this.pendingPhotoDisplayOrder || null;
    const initialDisplayOrder = this.displayOrderNumber(displayOrder) === null ? displayOrder : '00';
    this.savingPendingPhoto = true;
    this.api.saveWebcamPhoto(this.jobId, this.pendingPhotoData, name, null, imageType, initialDisplayOrder).pipe(
      switchMap((savedPhoto) => this.insertPhotoAtDisplayOrder(savedPhoto, displayOrder)),
    ).subscribe({
      next: (savedPhoto) => {
        this.savingPendingPhoto = false;
        this.channel?.post({ type: 'photo-saved' });
        this.photoSaved.emit(savedPhoto.label ?? name);
        this.clearPendingPhoto();
        this.toast.success('Success', 'Photo saved successfully.');
        this.loadPhotos();
      },
      error: (error) => {
        this.savingPendingPhoto = false;
        this.toast.error('Error', error?.message || 'Failed to save photo.');
      },
    });
  }

  cancelPendingPhoto(): void {
    if (this.savingPendingPhoto) return;
    this.clearPendingPhoto();
  }

  openPhotoEdit(photo: PropertyPhoto): void {
    if (this.savingPhotoEdit) return;
    this.editingPhoto = photo;
    this.editingPhotoName = photo.label || '';
    this.editingPhotoImageType = photo.imageType || '';
    this.editingPhotoDescription = photo.description || '';
    this.editingPhotoDisplayOrder = photo.displayOrder || '';
  }

  savePhotoEdit(): void {
    if (!this.editingPhoto || this.savingPhotoEdit) return;
    const editingPhoto = this.editingPhoto;
    const displayOrder = this.editingPhotoDisplayOrder || null;
    this.savingPhotoEdit = true;
    this.shiftDisplayOrdersForInsert(displayOrder, editingPhoto.id).pipe(
      switchMap(() => this.api.updatePhoto(this.jobId, editingPhoto.id, {
        label: this.editingPhotoName.trim() || null,
        description: editingPhoto.description ?? null,
        imageType: this.editingPhotoImageType.trim() || null,
        displayOrder,
      })),
    ).subscribe({
      next: (updated) => {
        this.savingPhotoEdit = false;
        this.photos = this.photos.map((photo) => photo.id === updated.id ? updated : photo);
        this.closePhotoEdit();
        this.channel?.post({ type: 'photo-saved' });
        this.photoSaved.emit(this.editingPhotoName.trim() || null);
        this.toast.success('Success', 'Photo details updated successfully.');
      },
      error: (error) => {
        this.savingPhotoEdit = false;
        this.toast.error('Error', error?.message || 'Failed to update photo details.');
      },
    });
  }

  closePhotoEdit(): void {
    if (this.savingPhotoEdit) return;
    this.editingPhoto = null;
    this.editingPhotoName = '';
    this.editingPhotoImageType = '';
    this.editingPhotoDescription = '';
    this.editingPhotoDisplayOrder = '';
  }

  deletePhoto(event: Event, photo: PropertyPhoto): void {
    event.stopPropagation();
    if (this.deletingPhotoId === photo.id) return;
    this.deletingPhotoId = photo.id;
    this.api.deletePhoto(photo.id).subscribe({
      next: () => {
        this.deletingPhotoId = null;
        if (this.editingPhoto?.id === photo.id) this.closePhotoEdit();
        this.photos = this.photos.filter((item) => item.id !== photo.id);
        this.channel?.post({ type: 'photo-saved' });
        this.photoSaved.emit();
        this.toast.success('Success', 'Photo deleted successfully.');
      },
      error: (error) => {
        this.deletingPhotoId = null;
        this.toast.error('Error', error?.message || 'Failed to delete photo.');
      },
    });
  }

  closeWindow(): void {
    this.formAlive = false;
    this.closed.emit();
    if (this.modalMode) return;
    try { window.close(); } catch {}
  }

  photoSource(photo: PropertyPhoto): string {
    return photo.photoUrl.startsWith('http') ? photo.photoUrl : photo.photoUrl;
  }

  private insertPhotoAtDisplayOrder(photo: PropertyPhoto, displayOrder: string | null) {
    if (this.displayOrderNumber(displayOrder) === null) return of(photo);
    return this.shiftDisplayOrdersForInsert(displayOrder, photo.id).pipe(
      switchMap(() => this.api.updatePhoto(this.jobId, photo.id, this.photoPayload(photo, displayOrder))),
    );
  }

  private shiftDisplayOrdersForInsert(displayOrder: string | null, excludedPhotoId?: number | string) {
    const insertOrder = this.displayOrderNumber(displayOrder);
    if (insertOrder === null) return of([]);

    return this.api.getPhotos(this.jobId).pipe(
      switchMap((photos) => {
        const photosToShift = photos
          .filter((photo) => photo.id !== excludedPhotoId)
          .filter((photo) => {
            const order = this.displayOrderNumber(photo.displayOrder);
            return order !== null && order >= insertOrder;
          })
          .sort((left, right) => (this.displayOrderNumber(right.displayOrder) ?? 0) - (this.displayOrderNumber(left.displayOrder) ?? 0));
        if (!photosToShift.length) return of([]);
        return from(photosToShift).pipe(
          concatMap((photo) => this.api.updatePhoto(this.jobId, photo.id, this.photoPayload(photo, this.nextDisplayOrder(photo.displayOrder)))),
          toArray(),
        );
      }),
    );
  }

  private photoPayload(photo: PropertyPhoto, displayOrder: string | null): Pick<PropertyPhoto, 'label' | 'description' | 'imageType' | 'displayOrder'> {
    return {
      label: photo.label ?? null,
      description: photo.description ?? null,
      imageType: photo.imageType ?? null,
      displayOrder,
    };
  }

  private nextDisplayOrder(displayOrder?: string | null): string | null {
    const order = this.displayOrderNumber(displayOrder);
    if (order === null) return displayOrder || null;
    return order >= 100 ? '00' : String(order + 1).padStart(2, '0');
  }

  private displayOrderNumber(displayOrder?: string | null): number | null {
    if (!displayOrder) return null;
    const numberValue = Number(displayOrder);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
  }

  private clearPendingPhoto(): void {
    this.pendingPhotoData = '';
    this.pendingPhotoName = '';
    this.pendingPhotoImageType = '';
    this.pendingPhotoDescription = '';
    this.pendingPhotoDisplayOrder = '';
  }

  private async refreshDevices(): Promise<void> {
    const devices = await navigator.mediaDevices.enumerateDevices();
    this.devices = devices.filter((device) => device.kind === 'videoinput');
    if (!this.selectedDeviceId || !this.devices.some((device) => device.deviceId === this.selectedDeviceId)) {
      this.selectedDeviceId = this.preferredBackCameraId();
    }
  }

  private preferredBackCameraId(): string {
    return this.devices.find((device) => /back|rear|environment|world/i.test(device.label))?.deviceId || '';
  }

  private cameraConstraints(): Array<boolean | MediaTrackConstraints> {
    const constraints: Array<boolean | MediaTrackConstraints> = [];
    if (this.selectedDeviceId) {
      constraints.push({ deviceId: { exact: this.selectedDeviceId } });
      constraints.push({ deviceId: { ideal: this.selectedDeviceId } });
    }
    constraints.push({ facingMode: { ideal: 'environment' } });
    constraints.push(true);
    return constraints;
  }

  private currentVideoDeviceId(): string {
    const track = this.stream?.getVideoTracks()[0];
    return track?.getSettings().deviceId || '';
  }

  private shouldTryNextConstraint(error: unknown): boolean {
    const name = error instanceof DOMException ? error.name : '';
    return name === 'OverconstrainedError' || name === 'NotFoundError' || name === 'AbortError';
  }

  private cameraErrorMessage(error: unknown): string {
    if (!window.isSecureContext) {
      return 'Camera access requires a secure browser context. Use http://localhost:4200 or HTTPS.';
    }

    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        return 'Camera permission was blocked. Allow camera access for localhost in the browser, then retry.';
      }
      if (error.name === 'NotFoundError') {
        return 'No camera device was found. Connect or enable a camera, then retry.';
      }
      if (error.name === 'NotReadableError') {
        return 'The camera is already in use or Windows blocked access. Close other camera apps and check Windows camera privacy settings.';
      }
      if (error.name === 'OverconstrainedError') {
        return 'The selected camera is not available. Retry with another camera.';
      }
    }

    return error instanceof Error && error.message
      ? `Unable to start the camera: ${error.message}`
      : 'Unable to start the selected camera.';
  }
}
